import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition, faUser, faTrash, faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import ActionItemDetailsPanel from '@frontend/Tasks/Tasks/Components/ActionItemDetailsPanel';
import Table from '../../../../../../Components/SecondaryTable/SecondaryTable';
import TaskService from '../../../../../../AppSettings/services/TaskService';
import toastService from '../../../../../../AppSettings/services/ToastService';
import { TaskByEmployee } from '../../../../../../Models/Tasks/TaskModels';
import type { PaginationProps } from '../../../../../../Models/Table/TableModels';
import ConfirmationModal from '../../../../../../Components/ConfirmationModal/ConfirmationModal';
import CreateTaskModal from '../../../../GeneralMatters/Components/TasksTable/Components/TaskData/CreateTask/CreateTaskModal';
import './TasksTable.css';

type SortKey = 'task' | 'status' | 'type' | 'assignedto' | 'duedate';
type SortDirection = 'asc' | 'desc' | null;

interface TasksTableProps {
  data: TaskByEmployee[];
  pageSize?: number;
  pagination?: PaginationProps;
  onTaskDeleted?: () => void;
  createTaskModalOpen?: boolean;
  onCloseCreateModal?: () => void;
  onOpenCreateModal?: () => void;
}

interface SortHeaderProps {
  label: string;
  colKey: SortKey;
}

const TasksTable: React.FC<TasksTableProps> = ({
  data,
  pageSize = 10,
  pagination,
  onTaskDeleted,
  createTaskModalOpen: controlledCreateModalOpen,
  onCloseCreateModal,
  onOpenCreateModal,
}) => {
  const { matterId } = useParams<{ matterId: string }>();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<TaskByEmployee | null>(null);
  const [sortBy, setSortBy] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTaskTitle, setSelectedTaskTitle] = useState<string | null>(null);
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);

  const isCreateModalControlled =
    controlledCreateModalOpen !== undefined && onCloseCreateModal && onOpenCreateModal;
  const createModalOpen = isCreateModalControlled ? controlledCreateModalOpen : isCreateTaskModalOpen;
  const handleCloseCreateModal = isCreateModalControlled
    ? onCloseCreateModal!
    : () => {
        setIsCreateTaskModalOpen(false);
        onTaskDeleted?.();
      };
  const openCreateModal = isCreateModalControlled ? onOpenCreateModal! : () => setIsCreateTaskModalOpen(true);

  const toggleSort = (key: SortKey): void => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
      return;
    }
    setSortDir((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));
  };

  const getSortValue = (key: SortKey, row: TaskByEmployee): string => {
    switch (key) {
      case 'task':
        return row.task_title || '';
      case 'status':
        return row.status || '';
      case 'type':
        return row.task_type || '';
      case 'assignedto':
        return row.assignedto || '';
      case 'duedate':
        return row.duedate || '';
      default:
        return '';
    }
  };

  const displayedData = useMemo(() => {
    if (!data || !sortBy || !sortDir) return data;
    const comparator = (a: TaskByEmployee, b: TaskByEmployee): number => {
      const va = getSortValue(sortBy, a);
      const vb = getSortValue(sortBy, b);

      if (sortBy === 'duedate') {
        const da = Date.parse(va);
        const db = Date.parse(vb);
        if (!Number.isNaN(da) && !Number.isNaN(db)) {
          return da - db;
        }
      }
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    };
    const sorted = [...data].sort(comparator);
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [data, sortBy, sortDir]);

  const SortHeader: React.FC<SortHeaderProps> = ({ label, colKey }) => (
    <div
      onClick={() => toggleSort(colKey)}
      style={{ cursor: "pointer", userSelect: "none", display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <span>{label}</span>
      <span>
        {sortBy === colKey && sortDir === 'asc' && <FontAwesomeIcon icon={faSortUp} />}
        {sortBy === colKey && sortDir === 'desc' && <FontAwesomeIcon icon={faSortDown} />}
        {(sortBy !== colKey || sortDir === null) && <FontAwesomeIcon icon={faSort} />}
      </span>
    </div>
  );

  const openSidebar = (task: TaskByEmployee): void => {
    setSelectedTask(task);
    setIsSidebarOpen(true);
  };

  const closeSidebar = (): void => {
    setIsSidebarOpen(false);
    setSelectedTask(null);
  };

  const handleEditTask = (updatedTask?: TaskByEmployee): void => {
    if (updatedTask && selectedTask && updatedTask.taskid === selectedTask.taskid) {
      setSelectedTask(updatedTask);
    }
    onTaskDeleted?.();
  };

  const handleDeleteFromPanel = (): void => {
    onTaskDeleted?.();
    closeSidebar();
  };

  const openModal = (taskId: string, taskTitle: string) => {
    setSelectedTaskId(taskId);
    setSelectedTaskTitle(taskTitle);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTaskId(null);
    setSelectedTaskTitle(null);
  };

  const confirmDelete = async () => {
    if (!selectedTaskId) return;
    try {
      const result = await TaskService.deleteTask(selectedTaskId);
      toastService.success('Task deleted successfully', {
        description: selectedTaskTitle ? `"${selectedTaskTitle}" has been removed.` : 'The task has been removed.',
      });
      // Call the callback to refresh the task list
      if (onTaskDeleted) {
        onTaskDeleted();
      }
      closeModal();
    } catch (error) {
      console.error('Error deleting task:', error);
      toastService.error('Failed to delete task', {
        description: 'Please try again or contact support if the issue persists.',
      });
    }
  };

  const getStatusClass = (status?: string | null) => {
    if (!status) return 'status-pill default';
    const normalized = status.toLowerCase();
    if (normalized.includes('progress')) return 'status-pill in-progress';
    if (normalized.includes('complete')) return 'status-pill complete';
    if (normalized.includes('pending')) return 'status-pill pending';
    if (normalized.includes('overdue')) return 'status-pill overdue';
    return 'status-pill default';
  };

  const formatDueDate = (value?: string | null) => {
    if (!value) {
      return { label: '—', subLabel: '' };
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return { label: value, subLabel: '' };
    }

    const now = new Date();
    const diffMs = parsed.getTime() - now.getTime();
    const days = Math.round(diffMs / (1000 * 60 * 60 * 24));

    const label = parsed.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (Number.isNaN(days)) {
      return { label, subLabel: '' };
    }

    if (days === 0) {
      return { label, subLabel: 'Due today' };
    }

    if (days > 0) {
      return {
        label,
        subLabel: `${days} day${days === 1 ? '' : 's'} left`,
      };
    }

    return {
      label,
      subLabel: `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`,
    };
  };

  if (!data) {
    return <></>;
  }

  const columns = [
    {
      header: (
        <SortHeader label={'Task'} colKey={'task'} />
      ),
      accessor: 'task_title' as keyof TaskByEmployee,
      render: (row: TaskByEmployee) => (
        <button
          className="secondary-Sub-Table-Link"
          title={row.task_title}
          onClick={() => openSidebar(row)}
        >
          {row.task_title}
        </button>
      )
    },
    {
      header: (
        <SortHeader label={'Status'} colKey={'status'} />
      ),
      render: (row: TaskByEmployee) => (
        <span className={getStatusClass(row.status)}>{row.status || '—'}</span>
      )
    },
    {
      header: (
        <SortHeader label={'Type'} colKey={'type'} />
      ),
      accessor: 'task_type' as keyof TaskByEmployee
    },
    {
      header: (
        <SortHeader label={'Assigned To'} colKey={'assignedto'} />
      ),
      accessor: 'assignedto' as keyof TaskByEmployee,
      icon: faUser as IconDefinition
    },
    {
      header: (
        <SortHeader label={'Due Date'} colKey={'duedate'} />
      ),
      render: (row: TaskByEmployee) => {
        const { label, subLabel } = formatDueDate(row.duedate);
        return (
          <div className="due-date-cell">
            <span className="due-date-label">{label}</span>
            {subLabel && <span className="due-date-sub">{subLabel}</span>}
          </div>
        );
      }
    },
    {
      header: '',
      render: (row: TaskByEmployee) => (
        <button
          className="delete-button"
          onClick={() => openModal(row.taskid || '', row.task_title || '')}
          aria-label="Delete Task"
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      )
    }
  ];

  return (
    <div className="tasks-table-wrapper">
      <Table 
        columns={columns}
        data={displayedData}
        emptyMessage="No tasks found"
        rowKey="taskid"
        pagination={pagination}
      />
      <ActionItemDetailsPanel
        task={selectedTask}
        onClose={closeSidebar}
        isOpen={isSidebarOpen}
        onEdit={handleEditTask}
        onDelete={handleDeleteFromPanel}
      />
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDelete}
        message={`Are you sure you want to delete the task "${selectedTaskTitle}"?`}
      />
      {matterId && (
        <CreateTaskModal
          isOpen={createModalOpen}
          onClose={() => {
            handleCloseCreateModal();
          }}
          matterId={matterId}
        />
      )}
    </div>
  );
};

export default TasksTable;

